import { Response } from "@models/Models";
import {
	ResultsMenuSport,
	ResultsFormattedData,
	MappedEvent,
	EventScoreAdditionalInfo,
} from "@data-types/results";
import { EventsRequestFilter } from "@api-types/events";
import BettingTypesApiService from "@api-services/platform/BettingTypesApiService";
import EventsApiService from "@api-services/platform/EventsApiService";
import EventsPerTournamentApiService from "@api-services/platform/EventsPerTournamentApiService";
import EventChangeTypesApiService from "@api-services/platform/EventChangeTypesApiService";
import EventChangesApiService from "@api-services/platform/EventChangesApiService";
import {
	BETTING_TYPES_LIST,
	createMenuFromTournamentEvents,
	findItemIndex,
	getOrderedEvents,
	makeCategory,
	makeFinalItemModel,
	makeMappedEvent,
	makeMobileTournament,
	makeSport,
} from "./helpers";

export default class ResultsService {
	constructor() {}

	static async getMenu(
		startDayFromToday: number
	): Promise<Response<ResultsMenuSport[]>> {
		const events = await EventsPerTournamentApiService.getEvents({
			startDayFromToday,
			endDayFromToday: null,
		});

		return new Response(createMenuFromTournamentEvents(events));
	}

	static async getBettingTypeIds() {
		const lookup = await BettingTypesApiService.findLookup();
		return lookup.item
			.filter((bt) => BETTING_TYPES_LIST.includes(bt.abrv))
			.map((bt) => bt.id);
	}

	static async getResults(
		filter: EventsRequestFilter,
		bettingTypeIds: string[]
	): Promise<Response<ResultsFormattedData<MappedEvent[]>[]>> {
		const fetchedEvents = await EventsApiService.getEventResults(
			filter,
			bettingTypeIds
		);
		const formattedResult = getOrderedEvents(
			fetchedEvents,
			filter.tournamentIds
		).reduce<ResultsFormattedData<MappedEvent[]>[]>((acc, item) => {
			const eventData = item.event;
			const itemWithSportAndCategoryIdx = findItemIndex(acc, item);
			const mappedEvent = makeMappedEvent(item);

			if (itemWithSportAndCategoryIdx > -1) {
				const existingItem = acc[itemWithSportAndCategoryIdx];

				const existingTournamentIdx =
					existingItem.tournaments.findIndex(
						(t) => t.id === eventData.tournament.id
					);

				if (existingTournamentIdx > -1) {
					existingItem.tournaments[existingTournamentIdx].events.push(
						mappedEvent
					);
				} else {
					existingItem.tournaments.push(
						makeMobileTournament(eventData, mappedEvent)
					);
				}
			} else {
				const sport = makeSport(eventData);
				const sportCategory = makeCategory(eventData);
				const tournament = makeMobileTournament(eventData, mappedEvent);
				const itemFinalModel = makeFinalItemModel(
					sport,
					sportCategory,
					tournament
				);

				acc.push(itemFinalModel);
			}

			return acc;
		}, []);

		return new Response(formattedResult);
	}

	static async getScoreChangeEventId() {
		const changeTypes = await EventChangeTypesApiService.getChangeTypes();

		return (
			changeTypes.item.find((item) => item.abrv === "score-change")?.id ||
			null
		);
	}

	static async getEventAdditionalInfo(
		eventId: string,
		scoreChangeEventId: string | null
	) {
		if (!scoreChangeEventId) return Promise.resolve(new Response(null));

		const fetchedResult = await EventChangesApiService.getChanges(
			[scoreChangeEventId],
			eventId
		);

		const changeInfo = fetchedResult.item.map<EventScoreAdditionalInfo>(
			(result) => ({
				data: JSON.parse(result.data),
				time: result.time,
			})
		);

		return new Response(changeInfo);
	}
}
